import React from "react";

// RUSH / fast-turnaround detection shared across the app.
const RUSH_RE = /\brush\b|\bexpedite[d]?\b|\basap\b|same[\s-]?day|24[\s-]?(?:hours?|hrs?)\b|due back within 24|within 24 hours/i;
export const isRushText = (t) => typeof t === "string" && RUSH_RE.test(t);

export function reqStats(order) {
  const reqs = order?.requirements || [];
  const done = order?.requirements_done || [];
  const total = reqs.length;
  const completed = reqs.reduce((n, _, i) => n + (done[i] ? 1 : 0), 0);
  const hasRush = reqs.some((r) => isRushText(r));
  const rushOpen = reqs.some((r, i) => isRushText(r) && !done[i]);
  return { total, completed, hasRush, rushOpen };
}

// Live 24-hour turnaround countdown for orders with a RUSH requirement.
export function TurnaroundTimer({ order, testid }) {
  const { hasRush } = reqStats(order);
  const [now, setNow] = React.useState(Date.now());
  React.useEffect(() => {
    if (!hasRush) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [hasRush]);
  if (!hasRush || !order?.created_at) return null;
  const deadline = new Date(order.created_at).getTime() + 24 * 3600 * 1000;
  const ms = deadline - now;
  const overdue = ms <= 0;
  const abs = Math.abs(ms);
  const h = Math.floor(abs / 3.6e6);
  const m = Math.floor((abs % 3.6e6) / 6e4);
  const s = Math.floor((abs % 6e4) / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  const urgent = overdue || ms < 2 * 3600 * 1000;
  const cls = overdue
    ? "border-red-500/50 bg-red-500/15 text-red-300"
    : urgent
    ? "border-amber-500/50 bg-amber-500/15 text-amber-300"
    : "border-teal/40 bg-teal/10 text-teal";
  return (
    <span data-testid={testid} title="24-hour turnaround deadline"
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-bold ${cls}`}>
      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
      {overdue ? `OVERDUE ${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(h)}:${pad(m)}:${pad(s)} left`}
    </span>
  );
}
export default function RequirementsProgress({ order, testid }) {
  const { total, completed, rushOpen } = reqStats(order);
  if (!total) return <span className="text-slate-300 text-xs">—</span>;
  const pct = Math.round((completed / total) * 100);
  const barColor = rushOpen ? "bg-red-500" : completed === total ? "bg-emerald-500" : "bg-teal";
  return (
    <div data-testid={testid} className="min-w-[92px]">
      <div className="flex items-center gap-1.5 mb-1">
        <span className={`text-[11px] font-semibold ${completed === total ? "text-emerald-300" : "text-slate-300"}`}>
          {completed} of {total}
        </span>
        {rushOpen && (
          <span className="inline-flex items-center rounded-full bg-red-500/20 px-1.5 py-0.5 text-[9px] font-bold text-red-300">RUSH</span>
        )}
      </div>
      <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${barColor}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}
